import { z } from 'zod';
import { STORE_CATEGORIES } from '../catalog.ts';
import { SHOPPING_CHECK_STATES, SHOPPING_RANGE_MODES } from '../planning.ts';
import { unitSchema } from './units.ts';

/** `YYYY-MM-DD` (a calendar day in the household's time zone). */
export const isoDateSchema = z.iso.date();
/** `HH:MM`. */
export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/** Planning and shopping rows as mirrored on the device (PLAN.md §4, §9). */
export const mealSlotSchema = z.object({
  id: z.uuid(),
  key: z.string(),
  /** Null = the default name of `key` in the reader's language. */
  label: z.string().nullable(),
  position: z.number().int(),
  defaultTime: timeOfDaySchema.nullable(),
  visible: z.boolean(),
  updatedAt: z.iso.datetime(),
});
export type MealSlot = z.infer<typeof mealSlotSchema>;

export const cookSessionSchema = z.object({
  id: z.uuid(),
  recipeId: z.uuid(),
  date: isoDateSchema,
  portions: z.number().int(),
  doneAt: z.iso.datetime().nullable(),
  /** What was different this time; shown in the cooking history. */
  notes: z.string().nullable(),
  /** A photo of what came out. */
  imageId: z.uuid().nullable(),
  createdBy: z.uuid().nullable(),
  updatedAt: z.iso.datetime(),
});
export type CookSession = z.infer<typeof cookSessionSchema>;

export const planEntrySchema = z.object({
  id: z.uuid(),
  date: isoDateSchema,
  slotKey: z.string(),
  /** Null = for everyone. */
  userId: z.uuid().nullable(),
  cookSessionId: z.uuid().nullable(),
  freeText: z.string().nullable(),
  portions: z.number(),
  updatedAt: z.iso.datetime(),
});
export type PlanEntry = z.infer<typeof planEntrySchema>;

export const shoppingCheckSchema = z.object({
  /** `shoppingCheckId(rangeStart, itemKey)`. */
  id: z.string(),
  rangeStart: isoDateSchema,
  itemKey: z.string(),
  state: z.enum(SHOPPING_CHECK_STATES),
  checkedAt: z.iso.datetime(),
  checkedBy: z.uuid().nullable(),
  updatedAt: z.iso.datetime(),
});
export type ShoppingCheck = z.infer<typeof shoppingCheckSchema>;

/** A shopping check's identity: its range start and item key. */
export const shoppingCheckId = (rangeStart: string, itemKey: string): string =>
  `${rangeStart}|${itemKey}`;

export function parseShoppingCheckId(id: string): { rangeStart: string; itemKey: string } | null {
  const rangeStart = id.slice(0, 10);
  if (id.charAt(10) !== '|' || !isoDateSchema.safeParse(rangeStart).success) return null;
  const itemKey = id.slice(11);
  return itemKey ? { rangeStart, itemKey } : null;
}

export const manualItemSchema = z.object({
  id: z.uuid(),
  text: z.string(),
  quantity: z.number().nullable(),
  unit: unitSchema.nullable(),
  ingredientId: z.uuid().nullable(),
  category: z.enum(STORE_CATEGORIES),
  checkedAt: z.iso.datetime().nullable(),
  createdBy: z.uuid().nullable(),
  updatedAt: z.iso.datetime(),
});
export type ManualItem = z.infer<typeof manualItemSchema>;

/** A recipe put on the shopping list without planning it (no day; cooked whenever). */
export const shoppingRecipeSchema = z.object({
  id: z.uuid(),
  recipeId: z.uuid(),
  portions: z.number().int(),
  createdBy: z.uuid().nullable(),
  updatedAt: z.iso.datetime(),
});
export type ShoppingRecipe = z.infer<typeof shoppingRecipeSchema>;

export const shoppingPrefsSchema = z.object({
  rangeMode: z.enum(SHOPPING_RANGE_MODES),
  rangeDays: z.number().int().min(1).max(28),
  /** ISO weekday: 1 = Monday … 7 = Sunday. */
  shoppingWeekday: z.literal([1, 2, 3, 4, 5, 6, 7]),
  updatedAt: z.iso.datetime(),
});
export type ShoppingPrefs = z.infer<typeof shoppingPrefsSchema>;

/** A household member as the plan needs them (per-person entries, portion multiplier). */
export const planMemberSchema = z.object({
  id: z.uuid(),
  displayName: z.string(),
  portionMultiplier: z.number(),
  updatedAt: z.iso.datetime(),
});
export type PlanMember = z.infer<typeof planMemberSchema>;
